import { Directive, HostListener, Input, Output, ElementRef, EventEmitter, Renderer2, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { DataService } from '../service/data.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CacheService } from '../service/cache.service';

@Directive({
	selector: '[textHover]'
})
export class TextHoverDirective implements OnInit, OnDestroy, OnChanges {
	private static activeHighlight: TextHoverDirective | null = null;
	// 全局唯一激活拖拽选区实例，整页只保留一个手柄
	private static activeDragInstance: TextHoverDirective | null = null;

	private lastChar: string | null = null;
	private originalText: string | null = null;
	private currentHighlightedWord: string | null = null;
	private hoverSubject: Subject<any>;
	private highlightElement: HTMLElement | null = null;
	private cleanupFn: () => void;
	private parts: any = [];
	@Input() textHoverDisable: boolean = false;

	// 拖拽功能总开关
	@Input() enableTextHoverDrag: boolean = false;

	@Output() public hoverCallback: EventEmitter<any> = new EventEmitter<any>();
	@Output() public leaveCallback: EventEmitter<any> = new EventEmitter<any>();
	@Output() public textHoverClick: EventEmitter<any> = new EventEmitter<any>();
	@Output() public afterTextHoverDrag: EventEmitter<any> = new EventEmitter();

	// 拖拽变量
	private selectRange: Range | null = null;
	private isDragging = false;
	private dragType: 'start' | 'end' | null = null;
	private dragOverlay: HTMLElement | null = null;
	
	// 存放動態開關的全局監聽器
	private globalUnlisteners: (() => void)[] = [];
	// 存放隨指令生命週期銷毀的常駐監聽器（如 document.click）
	private permanentUnlisteners: (() => void)[] = [];
	
	// 標記滑鼠是否在當前元素內
	private isMouseOverElement = false;

	constructor(
		private el: ElementRef,
		private datas: DataService,
		private renderer: Renderer2,
		public caches: CacheService
	) { }

	ngOnInit() {
		this.regHoverSubjectIfNeed();
		this.originalText = this.el.nativeElement.innerHTML;

		// 悬浮高亮框
		this.highlightElement = this.renderer.createElement('div');
		this.renderer.addClass(this.highlightElement, 'text-highlight');
		this.renderer.setStyle(this.highlightElement, 'position', 'absolute');
		this.renderer.setStyle(this.highlightElement, 'pointer-events', 'none');
		this.renderer.setStyle(this.highlightElement, 'z-index', '1000');
		this.renderer.setStyle(this.highlightElement, 'display', 'none');
		this.renderer.appendChild(document.body, this.highlightElement);

		if (this.enableTextHoverDrag) {
			this.initDragOverlay();
		}

		// 全局空白点击屬於常駐監聽，不隨滑鼠進出而解綁，但隨組件銷毀
		const docClickUnbind = this.renderer.listen(document, 'click', (e: MouseEvent) => {
			// 拖拽标记存在，本次click是拖拽后合成的，直接退出不清除
			if (this.isDragging) return;

			const target = e.target as HTMLElement;
			const hostEl = this.el.nativeElement;
			if (!hostEl.contains(target) && TextHoverDirective.activeDragInstance === this && !this.isDragging) {
				this.clearDragSelection();
				TextHoverDirective.activeDragInstance = null;
			}
		});
		this.permanentUnlisteners.push(docClickUnbind);

		this.cleanupFn = () => {
			if (this.highlightElement) {
				this.renderer.removeChild(document.body, this.highlightElement);
			}
			if (this.dragOverlay) {
				this.renderer.removeChild(document.body, this.dragOverlay);
			}
			this.unbindGlobalListeners();
			this.permanentUnlisteners.forEach(fn => fn());
			this.permanentUnlisteners = [];
		};
	}

	ngOnChanges(changes: SimpleChanges): void {
		const disableChange = changes['textHoverDisable'];
		if (!disableChange) return;

		const prevVal = disableChange.previousValue;
		const currVal = disableChange.currentValue;

		// 关闭功能 false → true
		if (!prevVal && currVal) {
			this.unbindGlobalListeners();
			if (this.dragOverlay) {
				this.dragOverlay.remove();
				this.dragOverlay = null;
			}

			this.isDragging = false;
			this.dragType = null;
			this.selectRange = null;

			this.removeHighlight();
			this.parts = [];

			if (TextHoverDirective.activeDragInstance === this) {
				TextHoverDirective.activeDragInstance = null;
			}
			return;
		}

		// 开启功能 true → false
		if (prevVal && !currVal) {
			if (this.dragOverlay) {
				this.dragOverlay.remove();
				this.dragOverlay = null;
			}

			if (this.enableTextHoverDrag) {
				this.initDragOverlay();
			}
			// 如果開啟功能時滑鼠正好在元素內，立刻啟用監聽
			if (this.isMouseOverElement) {
				this.bindGlobalListeners();
			}
		}
	}

	regHoverSubjectIfNeed(){
		if (!this.hoverSubject){
			this.hoverSubject = new Subject<any>();
			this.hoverSubject.pipe(debounceTime(50)).subscribe(event => {
				this.handleMouseMove(event);
			});
		}
	}

	/** 初始化拖拽容器 */
	private initDragOverlay() {
		this.dragOverlay = this.renderer.createElement('div');
		this.renderer.setStyle(this.dragOverlay, 'position', 'absolute');
		this.renderer.setStyle(this.dragOverlay, 'pointer-events', 'none');
		this.renderer.setStyle(this.dragOverlay, 'border', '2px solid #ff9900');
		this.renderer.setStyle(this.dragOverlay, 'background', 'rgba(255,153,0,0.15)');
		this.renderer.setStyle(this.dragOverlay, 'z-index', '1001');
		this.renderer.setStyle(this.dragOverlay, 'display', 'none');
		this.renderer.setStyle(this.dragOverlay, 'box-sizing', 'border-box');
		this.renderer.setStyle(this.dragOverlay, 'overflow', 'visible');
		this.renderer.setStyle(this.dragOverlay, 'transform-origin', '0 0');

		// 左侧拖拽点
		const leftDot = this.renderer.createElement('div');
		this.renderer.setStyle(leftDot, 'position', 'absolute');
		this.renderer.setStyle(leftDot, 'left', '-13px');
		this.renderer.setStyle(leftDot, 'width', '26px');
		this.renderer.setStyle(leftDot, 'height', '30px');
		this.renderer.setStyle(leftDot, 'background', '#ff6600');
		this.renderer.setStyle(leftDot, 'cursor', 'ew-resize');
		this.renderer.setStyle(leftDot, 'clip-path', 'path("M 19 2 C -7 12 6 26 16 23 C 23 20 22 8 20 0 Z")');
		this.renderer.setStyle(leftDot, 'pointer-events', 'auto');
		this.renderer.setStyle(leftDot, 'transform-origin', 'center top');
		this.renderer.listen(leftDot, 'mousedown', (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.startDrag('start');
		});

		// 右侧拖拽点
		const rightDot = this.renderer.createElement('div');
		this.renderer.setStyle(rightDot, 'position', 'absolute');
		this.renderer.setStyle(rightDot, 'right', '-13px');
		this.renderer.setStyle(rightDot, 'width', '26px');
		this.renderer.setStyle(rightDot, 'height', '30px');
		this.renderer.setStyle(rightDot, 'background', '#ff6600');
		this.renderer.setStyle(rightDot, 'cursor', 'ew-resize');
		this.renderer.setStyle(rightDot, 'clip-path', 'path("M 1 2 C 27 12 14 26 4 23 C -3 20 -2 8 0 0 Z")');
		this.renderer.setStyle(rightDot, 'pointer-events', 'auto');
		this.renderer.setStyle(rightDot, 'transform-origin', 'center top');
		this.renderer.listen(rightDot, 'mousedown', (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			this.startDrag('end');
		});

		// 触屏按下
		this.renderer.listen(leftDot, 'touchstart', (e: TouchEvent) => {
			e.preventDefault(); e.stopPropagation();
			this.startDrag('start');
		});
		this.renderer.listen(rightDot, 'touchstart', (e: TouchEvent) => {
			e.preventDefault(); e.stopPropagation();
			this.startDrag('end');
		});

		this.renderer.appendChild(this.dragOverlay, leftDot);
		this.renderer.appendChild(this.dragOverlay, rightDot);
		this.renderer.appendChild(document.body, this.dragOverlay);

		(this.dragOverlay as any).leftDot = leftDot;
		(this.dragOverlay as any).rightDot = rightDot;
	}

	/** 動態綁定全局監聽器 */
	private bindGlobalListeners() {
		if (this.globalUnlisteners.length > 0) return;

		const moveUnbind = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onGlobalDrag(e));
		const upUnbind = this.renderer.listen('document', 'mouseup', (e: MouseEvent) => {
			// 拖拽松手时阻止事件冒泡，从根源避免合成click传到document
			e.stopPropagation();
			this.stopDrag();
		});
		const touchMoveUnbind = this.renderer.listen('document', 'touchmove', (e: TouchEvent) => {
			if (this.isDragging) {
				e.preventDefault();
				const touch = e.touches[0];
				this.onGlobalDrag(touch);
			}
		});
		const touchEndUnbind = this.renderer.listen('document', 'touchend', (e: TouchEvent) => {
			e.stopPropagation();
			this.stopDrag();
		});

		this.globalUnlisteners.push(moveUnbind, upUnbind, touchMoveUnbind, touchEndUnbind);
	}

	/** 動態移除全局監聽器 */
	private unbindGlobalListeners() {
		this.globalUnlisteners.forEach(fn => {
			if (typeof fn === 'function') fn();
		});
		this.globalUnlisteners = [];
	}

	ngOnDestroy() {
		if (this.hoverSubject) {
			this.hoverSubject.unsubscribe();
		}
		if (this.cleanupFn) {
			this.cleanupFn();
		}
		if (TextHoverDirective.activeHighlight === this) {
			TextHoverDirective.activeHighlight = null;
		}
		if (TextHoverDirective.activeDragInstance === this) {
			TextHoverDirective.activeDragInstance = null;
		}
	}

	/** 清空拖拽选区 */
	public clearDragSelection() {
		this.selectRange = null;
		this.isDragging = false;
		this.dragType = null;
		if (this.dragOverlay) {
			this.renderer.setStyle(this.dragOverlay, 'display', 'none');
		}
	}

	@HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent): void {
		if (this.textHoverDisable) return;
		this.isMouseOverElement = true;
		this.bindGlobalListeners();
	}

	@HostListener('mousemove', ['$event']) onMouseMove(event: MouseEvent): void {
		if (this.textHoverDisable) return;
		if (this.isDragging) return;
		this.regHoverSubjectIfNeed();
		this.hoverSubject.next(event);
	}

	@HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent): void {
		if (this.textHoverDisable) return;
		this.isMouseOverElement = false;
		if (this.isDragging) return;
		
		// 仅隐藏悬浮高亮，拖拽选区保留
		this.removeHighlight();

		const leaveData = this.getLeaveData(event);
		if (leaveData) {
			this.leaveCallback.emit(leaveData);
		}
		this.lastChar = null;

		// 焦點離開且未在拖拽時，釋放監聽器
		this.unbindGlobalListeners();
	}

	@HostListener('click', ['$event']) onClick(event: any): void {
		if (this.textHoverDisable) return;
		if (this.enableTextHoverDrag) {
			this.handleClickSelectWord(event.clientX, event.clientY, event);
			this.oldHandleClick(event.clientX, event.clientY, event);
		}
		if (this.textHoverDisable) return;
		event.preventDefault();
		const touch = event.touches[0];
		let clientX: number, clientY: number;
		if ('touches' in event && event.touches.length > 0) {
			const touch = event.touches[0];
			clientX = touch.clientX;
			clientY = touch.clientY;
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}
		this.hoverSubject.next({ clientX: clientX, clientY: clientY } as MouseEvent);
	}

	@HostListener('touchend', ['$event']) onTouchEnd(event: TouchEvent): void {
		if (this.textHoverDisable) return;
		const touch = event.changedTouches[0];
		const element = document.elementFromPoint(touch.clientX, touch.clientY);
		if (!element || !this.el.nativeElement.contains(element)) {
			this.removeHighlight();
			this.leaveCallback.emit(this.getLeaveData(event));
			this.lastChar = null;
		}
	}

	@HostListener('touchstart', ['$event']) onTouchStart(event: TouchEvent): void {
		if (this.textHoverDisable) return;
		if (this.isDragging) return;
		const touch = event.touches[0];
		const target = event.target as HTMLElement;
		if (target && (target.style.cursor === 'ew-resize' || this.dragOverlay?.contains(target))) {
			return;
		}

		setTimeout(() => {
			if (this.isDragging) return;
			if (this.enableTextHoverDrag) {
				this.handleClickSelectWord(touch.clientX, touch.clientY, event);
			} else {
				this.oldHandleClick(touch.clientX, touch.clientY, event);
			}
		}, 100);
	}

	private oldHandleClick(x: number, y: number, event = null): void {
		if (TextHoverDirective.activeDragInstance && TextHoverDirective.activeDragInstance !== this) {
			TextHoverDirective.activeDragInstance.clearDragSelection();
			TextHoverDirective.activeDragInstance = null;
		}

		const charInfo = this.getCharacterAtPointWithVisualPosition(x, y);
		if (!charInfo || charInfo.character === '' || charInfo.globalOffset === -1) return;

		const fullText = this.el.nativeElement.innerText.replace(/\s+/g, ' ').trim();
		if (charInfo.globalOffset >= fullText.length) return;

		const clickedChar = fullText[charInfo.globalOffset] || '';
		if (!clickedChar) return;

		let targetWord = '';
		const matchedWords: { word: string, wordIndex: number, distance: number }[] = [];

		if (this.parts?.length > 0 && fullText) {
			for (const part of this.parts) {
				const word = part.text.trim();
				if (!word) continue;
				const wordLength = word.length;

				let wordIndex = fullText.indexOf(word);
				while (wordIndex !== -1) {
					const charInWordIndex = charInfo.globalOffset - wordIndex;
					const isCharInWord = charInWordIndex >= 0 && charInWordIndex < wordLength;
					const isCharMatched = isCharInWord && word[charInWordIndex] === clickedChar;

					if (isCharInWord && isCharMatched) {
						const wordRect = this.getWordVisualRect(word, wordIndex);
						if (wordRect) {
							const distance = this.calculateDistanceToRect(x, y, wordRect);
							if (x >= wordRect.left && x <= wordRect.right && y >= wordRect.top && y <= wordRect.bottom) {
								matchedWords.push({ word, wordIndex, distance });
							}
						}
					}
					wordIndex = fullText.indexOf(word, wordIndex + wordLength);
				}
			}

			if (matchedWords.length > 0) {
				matchedWords.sort((a, b) => a.distance - b.distance);
				targetWord = matchedWords[0].word;
			}
		}

		if (!targetWord) {
			targetWord = clickedChar;
		}

		if (targetWord) {
			this.textHoverClick.emit([{ clientX: x, clientY: y, target: event?.target }, targetWord, clickedChar]);
		}
	}

	private handleClickSelectWord(x: number, y: number, event = null): void {
		if (TextHoverDirective.activeDragInstance && TextHoverDirective.activeDragInstance !== this) {
			TextHoverDirective.activeDragInstance.clearDragSelection();
		}
		TextHoverDirective.activeDragInstance = this;

		const charInfo = this.getCharacterAtPointWithVisualPosition(x, y);
		if (!charInfo || charInfo.character === '' || charInfo.globalOffset === -1) return;
		const fullText = this.getAllTextNodes(this.el.nativeElement).map(n => n.textContent || '').join('');

		if (charInfo.globalOffset >= fullText.length) return;
		const clickedChar = fullText[charInfo.globalOffset] || '';
		if (!clickedChar) return;

		let targetRange: Range | null = null;
		const matchedWords: { word: string, wordIndex: number, distance: number }[] = [];

		if (this.parts?.length > 0 && fullText) {
			for (const part of this.parts) {
				const word = part.text.trim();
				if (!word) continue;
				const wordLength = word.length;

				let wordIndex = fullText.indexOf(word);
				while (wordIndex !== -1) {
					const charInWordIndex = charInfo.globalOffset - wordIndex;
					const isCharInWord = charInWordIndex >= 0 && charInWordIndex < wordLength;
					const isCharMatched = isCharInWord && word[charInWordIndex] === clickedChar;

					if (isCharInWord && isCharMatched) {
						const wordRect = this.getWordVisualRect(word, wordIndex);
						if (wordRect && this.isPointInRect(x, y, wordRect)) {
							const distance = this.calculateDistanceToRect(x, y, wordRect);
							matchedWords.push({ word, wordIndex, distance });
						}
					}
					wordIndex = fullText.indexOf(word, wordIndex + wordLength);
				}
			}

			if (matchedWords.length > 0) {
				matchedWords.sort((a, b) => a.distance - b.distance);
				const wordStartIdx = matchedWords[0].wordIndex;
				targetRange = this.getWordVisualRange(matchedWords[0].word, wordStartIdx);
			}
		}

		if (!targetRange) {
			targetRange = document.createRange();
			targetRange.setStart(charInfo.node, charInfo.localOffset);
			targetRange.setEnd(charInfo.node, charInfo.localOffset + 1);
		}

		this.selectRange = targetRange;
		this.renderDragOverlay();

		const realSelectText = this.selectRange.toString().trim();
		const emitWord = realSelectText || clickedChar;

		this.textHoverClick.emit([
			{ clientX: x, clientY: y, target: event?.target },
			emitWord,
			clickedChar
		]);
	}

	private startDrag(type: 'start' | 'end') {
		this.isDragging = true;
		this.dragType = type;
	}

	private onGlobalDrag(e: MouseEvent | Touch) {
		if (!this.isDragging || !this.selectRange || !this.dragType) return;

		let pos: CaretPosition | Range | null = null;
		if (document.caretPositionFromPoint) {
			pos = document.caretPositionFromPoint(e.clientX, e.clientY);
		} else if ((document as any).caretRangeFromPoint) {
			pos = (document as any).caretRangeFromPoint(e.clientX, e.clientY);
		}

		if (!pos) return;

		let container: Node;
		let offset: number;

		if ('offsetNode' in pos) {
			container = pos.offsetNode;
			offset = pos.offset;
		} else {
			container = pos.startContainer;
			offset = pos.startOffset;
		}

		if (!container) return;
		if (!this.el.nativeElement.contains(container)) return;

		try {
			const tempRange = this.selectRange.cloneRange();
			if (this.dragType === 'start') {
				tempRange.setStart(container, offset);
			} else {
				tempRange.setEnd(container, offset);
			}
			this.selectRange = tempRange;
			this.renderDragOverlay();
		} catch (err) {
			console.warn('拖拽选区设置失败', err);
		}
	}

	private stopDrag() {
		if (!this.isDragging || !this.selectRange) {
			this.isDragging = false;
			this.dragType = null;
			return;
		}

		try {
			if (this.selectRange.compareBoundaryPoints(Range.END_TO_START, this.selectRange) > 0) {
				const trueStartContainer = this.selectRange.endContainer;
				const trueStartOffset = this.selectRange.endOffset;
				const trueEndContainer = this.selectRange.startContainer;
				const trueEndOffset = this.selectRange.startOffset;

				this.selectRange.setStart(trueStartContainer, trueStartOffset);
				this.selectRange.setEnd(trueEndContainer, trueEndOffset);
				this.renderDragOverlay();
			}
		} catch (e) {
			console.warn('校正Range顺序失败', e);
		}

		const selectedText = this.selectRange.toString().trim();
		this.afterTextHoverDrag.emit([this.selectRange.cloneRange(), selectedText, this.lastChar]);

		setTimeout(()=>{
			this.isDragging = false;
			this.dragType = null;

			// 若鬆開手標時，滑鼠早已移出元素外，在此補做解綁動作
			if (!this.isMouseOverElement) {
				this.unbindGlobalListeners();
			}
		},100);
	}

	private renderDragOverlay() {
		if (!this.dragOverlay || !this.selectRange) return;
		const rect = this.selectRange.getBoundingClientRect();

		const maxSafeSize = 3000;
		if (rect.width > maxSafeSize || rect.height > maxSafeSize) {
			this.renderer.setStyle(this.dragOverlay, 'display', 'none');
			return;
		}

		const scrollX = window.scrollX;
		const scrollY = window.scrollY;
		const baseLeft = rect.left + scrollX;
		const baseTop = rect.top + scrollY;

		this.renderer.setStyle(this.dragOverlay, 'display', 'block');
		this.renderer.setStyle(this.dragOverlay, 'left', `${baseLeft}px`);
		this.renderer.setStyle(this.dragOverlay, 'top', `${baseTop}px`);
		this.renderer.setStyle(this.dragOverlay, 'width', `${rect.width}px`);
		this.renderer.setStyle(this.dragOverlay, 'height', `${rect.height}px`);

		let rotateDeg = 0;
		if (this.selectRange?.startContainer) {
			rotateDeg = this.getTotalRotateDeg(this.selectRange.startContainer, this.el.nativeElement);
		}

		if (rotateDeg !== 0) {
			this.renderer.setStyle(this.dragOverlay, 'transform', `rotate(${rotateDeg}deg)`);
		} else {
			this.renderer.removeStyle(this.dragOverlay, 'transform');
		}

		const dotTop = rect.height + 6;
		const leftDot = (this.dragOverlay as any).leftDot;
		const rightDot = (this.dragOverlay as any).rightDot;

		if (leftDot) {
			this.renderer.setStyle(leftDot, 'top', `${dotTop}px`);
			this.renderer.setStyle(leftDot, 'transform', `rotate(${rotateDeg}deg)`);
			this.renderer.setStyle(leftDot, 'transform-origin', 'center top');
		}
		if (rightDot) {
			this.renderer.setStyle(rightDot, 'top', `${dotTop}px`);
			this.renderer.setStyle(rightDot, 'transform', `rotate(${rotateDeg}deg)`);
			this.renderer.setStyle(rightDot, 'transform-origin', 'center top');
		}
	}

	private getTotalRotateDeg(fromNode: Node, rootHost: HTMLElement): number {
		let current: HTMLElement | null = fromNode.parentElement;
		let totalRotate = 0;

		while (current && rootHost.contains(current)) {
			const computed = getComputedStyle(current);
			const transform = computed.transform;
			if (transform && transform !== 'none') {
				try {
					const matrix = new (window.WebKitCSSMatrix || (window as any).CSSMatrix)(transform);
					const deg = Math.atan2(matrix.m21, matrix.m11) * (180 / Math.PI);
					totalRotate += deg;
				} catch (e) { }
			}
			current = current.parentElement;
		}

		totalRotate = totalRotate % 360;
		if (totalRotate > 180) totalRotate -= 360;
		if (totalRotate < -180) totalRotate += 360;
		return Math.round(totalRotate);
	}

	public clearSelection() {
		this.selectRange = null;
		if (this.dragOverlay) {
			this.renderer.setStyle(this.dragOverlay, 'display', 'none');
		}
	}

	private getWordVisualRange(word: string, startIndex: number): Range | null {
		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		let currentGlobalIndex = 0;
		let targetNode: Node | null = null;
		let nodeStartOffset = 0;

		for (const node of textNodes) {
			if (node.nodeType !== Node.TEXT_NODE) continue;
			const nodeText = node.textContent || '';
			const len = nodeText.length;

			if (startIndex >= currentGlobalIndex && startIndex < currentGlobalIndex + len) {
				targetNode = node;
				nodeStartOffset = startIndex - currentGlobalIndex;
				break;
			}
			currentGlobalIndex += len;
		}

		if (!targetNode) return null;

		try {
			const range = document.createRange();
			const nodeTextLen = targetNode.textContent?.length || 0;
			const nodeEndOffset = Math.min(nodeStartOffset + word.length, nodeTextLen);
			range.setStart(targetNode, nodeStartOffset);
			range.setEnd(targetNode, nodeEndOffset);
			return range;
		} catch (e) {
			console.warn('创建Range失败', e);
			return null;
		}
	}

	private calculateDistanceToRect(x: number, y: number, rect: DOMRect): number {
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;
		return Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
	}

	private handleMouseMove(event: MouseEvent): void {
		if (this.textHoverDisable || !this.highlightElement) return;
		if (this.enableTextHoverDrag && this.isDragging) return;

		const hoverData = this.getHoverData(event);
		const fullText = this.getAllTextNodes(this.el.nativeElement).map(n => n.textContent || '').join('');
		if (!hoverData && !fullText) {
			this.currentHighlightedWord = null;
			return;
		}

		if (fullText) {
			const cacheKey = `nlp_${fullText}`;
			const cachedNlpData = this.caches.getCachedData(cacheKey);
			const handleNlpData = (res) => {
				if (!res?.output?.[0]?.items || !Array.isArray(res.output[0].items)) {
					this.parts = [];
					return;
				}
				this.parts = res.output[0].items;

				let index = hoverData?.globalOffset || hoverData?.index || 0;
				const items = this.parts;
				if (!Array.isArray(items) || items.length === 0) return;

				for (let item of items) {
					const itemIndex = this.findWordIndexByVisualPosition(fullText, item.text, event.clientX, event.clientY);
					if (itemIndex !== -1 && index >= itemIndex && index < itemIndex + item.text.length) {
						const occurrence = this.countOccurrencesUpToIndex(fullText, item.text, index);
						this.highlightWord(item.text, occurrence, fullText, index);
						break;
					}
				}
			};
			if (cachedNlpData) {
				handleNlpData(cachedNlpData);
			} else {
				this.datas.post("NLP.analyze_chinese_text_array", [[fullText]]).subscribe({
					next: (res: any) => {
						this.caches.cacheData(cacheKey, res);
						handleNlpData(res);
					},
					error: () => {
						this.parts = [];
					}
				});
			}

			if (hoverData) {
				this.hoverCallback.emit(hoverData);
			}
		}
		this.lastChar = hoverData?.char || fullText[0];
	}

	private highlightWord(word: string, occurrence: number, fullText: string, charIndex: number): void {
		if (!this.highlightElement) return;
		TextHoverDirective.activeHighlight = this;

		const wordIndex = this.findNthOccurrence(fullText, word, occurrence);
		if (wordIndex === -1) return;

		const wordRect = this.getWordVisualRect(word, wordIndex);
		if (!wordRect) return;

		this.currentHighlightedWord = word;
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

		this.renderer.setStyle(this.highlightElement, 'display', 'block');
		this.renderer.setStyle(this.highlightElement, 'left', `${wordRect.left + scrollLeft}px`);
		this.renderer.setStyle(this.highlightElement, 'top', `${wordRect.top + scrollTop}px`);
		this.renderer.setStyle(this.highlightElement, 'width', `${wordRect.width}px`);
		this.renderer.setStyle(this.highlightElement, 'height', `${wordRect.height}px`);
	}

	private removeHighlight(): void {
		if (this.highlightElement) {
			this.renderer.setStyle(this.highlightElement, 'display', 'none');
		}
		this.currentHighlightedWord = null;
		if (TextHoverDirective.activeHighlight === this) {
			TextHoverDirective.activeHighlight = null;
		}
	}

	private countOccurrencesUpToIndex(text: string, word: string, charIndex: number): number {
		let count = 0;
		let currentIndex = 0;
		while (currentIndex <= charIndex) {
			const foundIndex = text.indexOf(word, currentIndex);
			if (foundIndex === -1 || foundIndex > charIndex) break;
			count++;
			currentIndex = foundIndex + word.length;
		}
		return count;
	}

	private findTextNode(element: Node, text: string): Node | null {
		const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
		let node: Node | null;
		while (node = walker.nextNode()) {
			if (node.textContent?.includes(text)) return node;
		}
		return null;
	}

	private findNthOccurrence(text: string, word: string, n: number): number {
		let count = 0;
		let index = -1;
		while (count < n) {
			index = text.indexOf(word, index + 1);
			if (index === -1) return -1;
			count++;
		}
		return index;
	}

	private getLeaveData(event: MouseEvent | TouchEvent): any {
		return {
			event,
			char: this.lastChar || '',
			index: -1,
			fullText: ''
		};
	}

	private getHoverData(event: MouseEvent): any {
		let caretPos = null;
		if (document.caretPositionFromPoint) {
			caretPos = document.caretPositionFromPoint(event.clientX, event.clientY);
		} else if ((document as any).caretRangeFromPoint) {
			caretPos = (document as any).caretRangeFromPoint(event.clientX, event.clientY);
		}

		if (!caretPos) {
			const fullText = this.el.nativeElement.innerText.trim();
			if (fullText) {
				return {
					event,
					char: fullText[0] || '',
					index: 0,
					globalOffset: 0,
					fullText: fullText
				};
			}
			return null;
		}

		const textNode = 'startContainer' in caretPos ? caretPos.startContainer : caretPos.offsetNode;
		if (!textNode) return null;
		const localOffset = 'startOffset' in caretPos ? caretPos.startOffset : caretPos.offset;

		if (textNode.nodeType !== Node.TEXT_NODE) {
			const fullText = this.el.nativeElement.innerText.trim();
			if (fullText) {
				return {
					event,
					char: fullText[0] || '',
					index: 0,
					globalOffset: 0,
					fullText: fullText
				};
			}
			return null;
		}

		let el: any = event.target;
		el = el.parentElement ? el.parentElement : el;
		if (el.querySelector('a[href]')) return null;

		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		const nodeIndex = textNodes.findIndex(node => node === textNode);
		let globalOffset = 0;
		for (let i = 0; i < nodeIndex; i++) {
			globalOffset += textNodes[i].textContent?.length || 0;
		}
		globalOffset += localOffset;

		const fullText = this.getAllTextFromNode(this.el.nativeElement);
		let char = fullText[globalOffset] || '';
		if (/[\u3000-\u303F\uff01-\uff60]/.test(char)) {
			char = fullText[globalOffset + 1] || fullText[0] || '';
		}

		return {
			event,
			char: char,
			index: localOffset,
			globalOffset: globalOffset,
			fullText: fullText
		};
	}

	private getCharacterAtPointWithVisualPosition(x: number, y: number) {
		let caretPos: CaretPosition | Range | null = null;
		try {
			if (document.caretPositionFromPoint) {
				caretPos = document.caretPositionFromPoint(x, y);
			} else if ((document as any).caretRangeFromPoint) {
				caretPos = (document as any).caretRangeFromPoint(x, y);
			}
		} catch (e) {
			console.warn('获取CaretPosition失败', e);
			return null;
		}

		if (!caretPos) return null;
		const offsetNode = (caretPos as CaretPosition).offsetNode || (caretPos as Range).startContainer;
		const localOffset = (caretPos as CaretPosition).offset || (caretPos as Range).startOffset;

		if (offsetNode.nodeType !== Node.TEXT_NODE) {
			return {
				element: offsetNode.parentElement,
				node: offsetNode,
				localOffset: localOffset,
				globalOffset: -1,
				character: '',
				rect: null
			};
		}

		const nodeText = (offsetNode.textContent || '').replace(/\s+/g, ' ').trim();
		const nodeTextLength = nodeText.length;
		if (nodeTextLength === 0) return null;

		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		const nodeIndex = textNodes.findIndex(node => node === offsetNode);
		let globalOffset = 0;
		for (let i = 0; i < nodeIndex; i++) {
			const prevNodeText = (textNodes[i].textContent || '').replace(/\s+/g, ' ').trim();
			globalOffset += prevNodeText.length;
		}

		let clickedCharIndex = localOffset;
		clickedCharIndex = Math.max(0, Math.min(clickedCharIndex, nodeTextLength - 1));

		const range = document.createRange();
		range.setStart(offsetNode, clickedCharIndex);
		range.setEnd(offsetNode, clickedCharIndex + 1);
		const charRect = range.getBoundingClientRect();

		globalOffset += clickedCharIndex;
		const charClicked = nodeText[clickedCharIndex] || '';

		return {
			element: offsetNode.parentElement,
			node: offsetNode,
			localOffset: clickedCharIndex,
			globalOffset: globalOffset,
			character: charClicked,
			rect: charRect
		};
	}

	private getWordVisualRect(word: string, startIndex: number): DOMRect | null {
		const range = this.getWordVisualRange(word, startIndex);
		if (!range) return null;
		try {
			return range.getBoundingClientRect();
		} catch (e) {
			console.warn('获取Word Rect失败', e);
			return null;
		}
	}

	private isPointInRect(x: number, y: number, rect: DOMRect): boolean {
		return x >= rect.left - 5 && x <= rect.right + 5 && y >= rect.top - 5 && y <= rect.bottom + 5;
	}

	private findWordIndexByVisualPosition(fullText: string, word: string, x: number, y: number): number {
		let wordIndex = fullText.indexOf(word);
		while (wordIndex !== -1) {
			const rect = this.getWordVisualRect(word, wordIndex);
			if (rect && this.isPointInRect(x, y, rect)) return wordIndex;
			wordIndex = fullText.indexOf(word, wordIndex + word.length);
		}
		return -1;
	}

	private getCharacterAtPoint(x: number, y: number) {
		return this.getCharacterAtPointWithVisualPosition(x, y);
	}

	private getAllTextFromNode(element: Node): string {
		let text = '';
		for (const node of Array.from(element.childNodes)) {
			if (node.nodeType === Node.TEXT_NODE) text += node.textContent || '';
			else if (node.nodeType === Node.ELEMENT_NODE) text += this.getAllTextFromNode(node);
		}
		return text;
	}

	private getAllTextNodes(element: Node): Node[] {
		const textNodes: Node[] = [];
		for (const node of Array.from(element.childNodes)) {
			if (node.nodeType === Node.TEXT_NODE) textNodes.push(node);
			else if (node.nodeType === Node.ELEMENT_NODE) textNodes.push(...this.getAllTextNodes(node));
		}
		return textNodes;
	}
}